import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../utils/api';

const Profile = ({ user, onProfileUpdate }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [motherMaidenName, setMotherMaidenName] = useState('');
  const [cityBorn, setCityBorn] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const changingPassword = newPassword.trim().length > 0;
    const changingSecurity = motherMaidenName.trim() !== '' || cityBorn.trim() !== '';

    if (changingPassword) {
      if (newPassword.length < 6) {
        setError('New password must be at least 6 characters');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('New password and confirmation do not match');
        return;
      }
      if (!currentPassword && (changingPassword || changingSecurity)) {
        setError('Current password is required to change password or security answers');
        return;
      }
    }
    if ((changingSecurity || changingPassword) && !currentPassword) {
      setError('Current password is required to change password or security answers');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        phone: phone.trim() || null
      };
      if (currentPassword) payload.current_password = currentPassword;
      if (changingPassword) payload.new_password = newPassword.trim();
      if (motherMaidenName.trim()) payload.mother_maiden_name = motherMaidenName.trim();
      if (cityBorn.trim()) payload.city_born = cityBorn.trim();

      const res = await api.put('/auth/profile', payload);
      onProfileUpdate(res.data.user);
      setSuccess('Profile updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMotherMaidenName('');
      setCityBorn('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: '440px', margin: '24px auto' }}>
      <h2>Account settings</h2>
      <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
        Update your name, phone, password, or security question answers.
      </p>
      {error && <div style={{ color: 'var(--danger, #dc3545)', marginBottom: '15px' }}>{error}</div>}
      {success && <div style={{ color: 'var(--success, #28a745)', marginBottom: '15px' }}>{success}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input type="email" value={user.email || ''} disabled style={{ background: '#eee', color: '#666' }} />
          <small style={{ color: '#666' }}>Email cannot be changed.</small>
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <hr style={{ margin: '24px 0 16px', border: 'none', borderTop: '1px solid #ddd' }} />
        <h3 style={{ marginBottom: '12px', fontSize: '1.1rem' }}>Change password</h3>
        <div className="form-group">
          <label>Current password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Required only if changing password or security answers"
            autoComplete="current-password"
          />
        </div>
        <div className="form-group">
          <label>New password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Leave blank to keep current"
            autoComplete="new-password"
          />
        </div>
        <div className="form-group">
          <label>Confirm new password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Leave blank to keep current"
            autoComplete="new-password"
          />
        </div>

        <hr style={{ margin: '24px 0 16px', border: 'none', borderTop: '1px solid #ddd' }} />
        <h3 style={{ marginBottom: '12px', fontSize: '1.1rem' }}>Security questions</h3>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
          Used for password reset. Enter new answers only if you want to change them. Current password required above.
        </p>
        <div className="form-group">
          <label>Mother&apos;s maiden name</label>
          <input
            type="text"
            value={motherMaidenName}
            onChange={(e) => setMotherMaidenName(e.target.value)}
            placeholder="Leave blank to keep current"
            autoComplete="off"
          />
        </div>
        <div className="form-group">
          <label>City born</label>
          <input
            type="text"
            value={cityBorn}
            onChange={(e) => setCityBorn(e.target.value)}
            placeholder="Leave blank to keep current"
            autoComplete="off"
          />
        </div>

        <div style={{ marginTop: '24px' }}>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Profile;
